import { useMutation } from '@apollo/client';
import { MutationResponse, SendMagicLinkInput } from '@bybeam/platform-types';
import Button from '@platform-ui-common/components/Button/index.js';
import ErrorDisplay from '@platform-ui-common/components/ErrorDisplay/index.js';
import FormContainer from '@platform-ui-common/components/FormContainer/index.js';
import Icon from '@platform-ui-common/components/Icon/index.js';
import Message from '@platform-ui-common/components/Message/index.js';
import Typography from '@platform-ui-common/components/Typography/index.js';
import usePartner from '@platform-ui-common/hooks/contexts/usePartner.js';
import useAuth from '@platform-ui/auth/hooks/useAuth/index.js';
import { useUserHomeRoute } from '@platform-ui/utilities/Routes.js';
import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom-v5-compat';
import SendMagicLink from '../graphql/SendMagicLinkMutation.graphql';

function EmailSent() {
  const { state } = useLocation();
  const email = state?.email;
  const partner = usePartner();
  const {
    user,
    identity: {
      auth: { currentUser },
      tenantId,
    },
  } = useAuth();
  const homeRoute = useUserHomeRoute();

  const [error, setError] = useState(null);
  const [sent, setSent] = useState(false);

  const [sendLink, mutationResults] = useMutation<
    { identity: { sendMagicLink: MutationResponse } },
    { input: SendMagicLinkInput }
  >(SendMagicLink);

  useEffect(() => {
    if (mutationResults.called && !mutationResults.loading) {
      if (mutationResults?.data?.identity?.sendMagicLink?.metadata?.status === 200) setSent(true);
      else setError('Error sending magic link');
    }
  });

  const submit = async () => {
    try {
      await sendLink({
        variables: {
          input: {
            email,
            partnerId: partner.id,
            tenantId,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          },
        },
      });
    } catch (e) {
      console.error(e);
      setError(e);
    }
  };

  const loading = mutationResults.called && mutationResults.loading;

  if (user && currentUser) {
    return <Navigate to={homeRoute} replace />;
  }

  return (
    <FormContainer title="Email Sent" onSubmit={submit}>
      <div className="w-full flex flex-col grow gap-x-9 lg:flex-row lg:mt-8 lg:w-3/5">
        <div className="text-center">
          <Icon type="adminPanelOutline" size="XXL" />
        </div>
        <div className="w-full flex flex-col mt-8 lg:mt-0 grow">
          <div className="flex flex-col gap-y-6 pb-10 grow lg:grow-0">
            <Typography tag="div" variant="body">
              <p>
                We sent an email to <b>{email}</b> with a link to sign in.
              </p>

              <br />
              <p>
                If you don't see an email in the next five minutes, check your spam filter or try
                the resend button below.
              </p>
            </Typography>
          </div>
          <div className="w-full flex flex-col gap-y-4 items-center lg:items-start text-center lg:text-left">
            {error && (
              <ErrorDisplay errors={error} visible={!!error} message="Error resending magic link" />
            )}
            {!error && sent && <Message variant="success">Email Sent!</Message>}
            <Button disabled={loading} loading={loading} type="submit">
              Resend Email
            </Button>
            <Button variant="outline" link={`mailto:${partner.email}`} external>
              Contact Us
            </Button>
          </div>
        </div>
      </div>
    </FormContainer>
  );
}

export default EmailSent;
